import { useState } from "preact/hooks";
import { store, stored, useStored } from "./storage.ts";

const VIEW_KEY = "revisa.items.view";

/** Below Tailwind's `sm`: phones, where cards are the default. */
const SMALL = "(width < 40rem)";

/** How the items list shows: a table, a grid of cards, or a board (cards in a column per value of a choice field). */
export type ListView = "table" | "cards" | "board";

const VIEWS: readonly string[] = ["table", "cards", "board"] satisfies ListView[];

/** The list's view: the choice remembered in this browser for `screen` (else the last one made), by default
 *  cards on phones and the table on wider screens. */
export function useListView(screen?: string): [ListView, (view: ListView) => void] {
  // Each screen its own (the last one chosen anywhere until then).
  const key = screen ? `${VIEW_KEY}.${screen}` : VIEW_KEY;
  const [view, setView] = useState<ListView>(() => {
    const saved = stored(key) ?? stored(VIEW_KEY);
    if (saved && VIEWS.includes(saved)) {
      return saved as ListView;
    }
    return typeof matchMedia === "function" && matchMedia(SMALL).matches ? "cards" : "table";
  });
  const set = (v: ListView) => {
    setView(v);
    store(key, v);
    store(VIEW_KEY, v);
  };
  return [view, set];
}

const CONVERSATIONS_KEY = "revisa.items.conversations";

/** Whether the items list shows each email conversation as one row (its latest email): remembered in this browser, on
 *  unless turned off. */
export function useConversations(): [boolean, (on: boolean) => void] {
  return useStored(CONVERSATIONS_KEY, (raw) => raw !== "false");
}

const UNMATCHED_KEY = "revisa.items.unmatchedFolders";

/** Whether a filtered items list still starts with every folder, those holding no match too: remembered in this
 *  browser, off unless turned on. */
export function useUnmatchedFolders(): [boolean, (on: boolean) => void] {
  return useStored(UNMATCHED_KEY, (raw) => raw === "true");
}
